import { t } from './i18n.mjs?v=20260806.100';

// Team is frozen and hidden (Alan, 2026-08-07). The page and its module stay exactly
// where they are - this removes the way in, it does not delete the work. The route is
// turned away in app.js too, so a bookmark cannot walk past the missing button: a page
// hidden only from the menu is still a page.
//
// Status came back on 2026-08-08 as Company Overview, on the same #/status route so
// the bookmarks that were turned away now land somewhere. Every internal role has it;
// a customer does not, and a driver has no web console to put it in.
const NAVIGATION = Object.freeze({
  admin: [
    { route: '#/dashboard', label: 'Dashboard', labelKey: 'nav.dashboard', icon: 'dashboard', color: 'blue' },
    { route: '#/status', label: 'Companies', labelKey: 'nav.companies', icon: 'customers', color: 'purple' },
    { route: '#/customer-quotes', label: 'Quotes', labelKey: 'nav.customerQuotes', icon: 'shipment', color: 'teal' },
    { route: '#/chassis', label: 'Chassis', labelKey: 'nav.chassis', icon: 'chassis', color: 'green' },
    { route: '#/admin-users', label: 'Settings', labelKey: 'nav.settings', icon: 'settings', color: 'indigo' },
  ],
  // Quotes is not on Dispatch's bar (Alan, 2026-10-12: "这三个页面在调度页面不用显示"). The plus and Bulk Add went
  // with it that day and came back on 2026-10-14 ("文员调度这些之类的都有权利加柜子").
  dispatcher: [
    { route: '#/dashboard', label: 'Dashboard', labelKey: 'nav.dashboard', icon: 'dashboard', color: 'blue' },
    { route: '#/status', label: 'Companies', labelKey: 'nav.companies', icon: 'customers', color: 'purple' },
    { route: '#/chassis', label: 'Chassis', labelKey: 'nav.chassis', icon: 'chassis', color: 'teal' },
  ],
  clerk: [
    { route: '#/dashboard', label: 'Dashboard', labelKey: 'nav.dashboard', icon: 'dashboard', color: 'blue' },
    { route: '#/status', label: 'Companies', labelKey: 'nav.companies', icon: 'customers', color: 'purple' },
    { route: '#/chassis', label: 'Chassis', labelKey: 'nav.chassis', icon: 'chassis', color: 'teal' },
  ],
  // Finance owns the quotes: setting a delivery address sets a price, and the
  // callable's manager roles are admin and finance. The page has offered Finance
  // the add form all along - there was simply no route and no button to reach it
  // by, so the permission existed and the door did not.
  finance: [
    { route: '#/dashboard', label: 'Dashboard', labelKey: 'nav.dashboard', icon: 'dashboard', color: 'blue' },
    { route: '#/status', label: 'Companies', labelKey: 'nav.companies', icon: 'customers', color: 'purple' },
    { route: '#/customer-quotes', label: 'Quotes', labelKey: 'nav.customerQuotes', icon: 'shipment', color: 'indigo' },
    { route: '#/chassis', label: 'Chassis', labelKey: 'nav.chassis', icon: 'chassis', color: 'teal' },
  ],
  customer: [
    { route: '#/dashboard', label: 'Dashboard', labelKey: 'nav.dashboard', icon: 'dashboard', color: 'blue' },
  ],
});

// A Task enters the system in exactly one way: the customer files it (Alan,
// 2026-08-02) - reversed on 2026-08-07. Every role except Driver can open one now:
// the work arrives by phone and by email as often as it arrives through the portal,
// and the person holding the phone was not allowed to write it down.
//
// Staff must name the Customer, and the Delivery Address list is that Customer's
// approved one. A Customer opening their own Task is still locked to themselves -
// there is no picker for them, and the callable takes their identity from the
// token rather than the form.
//
// The launcher is only the door. OPERATIONS_ADD_TASK_ROLES in functions/src/index.ts
// is the permission, and it was opened in the same change - a launcher that is
// merely shown is not a permission either.
// The plus adds a Task and nothing else (Alan, 2026-08-09: "这个加号只能加任务").
// Customers and Drivers are created from their own pages; putting them here made
// the one button anyone presses hourly into a menu they had to read first.
//
// The launcher is only the door. OPERATIONS_ADD_TASK_ROLES in functions/src/index.ts
// is the permission.
const ADD_TASK_ITEM = Object.freeze({
  type: 'task', route: '#/create/task', label: 'Add Task', labelKey: 'nav.addTask', icon: 'shipment', color: 'blue',
});
// 批量新增 (Alan, 2026-09-26: "一个专门的tab这个页面可以批量上传新的柜子或者单独
// 一个个的"). Its own button rather than a second item under the plus, which stays a
// single press into Add Task. The same four roles OPERATIONS_ADD_TASK_ROLES lets
// create a Task, because every Container it adds is made by that callable.
const BULK_ADD_ITEM = Object.freeze({
  type: 'bulk', route: '#/create/bulk', label: 'Bulk Add', labelKey: 'nav.bulkAdd', icon: 'shipment', color: 'teal',
});
const BULK_ADD_ROLES = new Set(['admin', 'dispatcher', 'clerk', 'finance']);

export function bulkAddAvailableForRole(role) {
  return BULK_ADD_ROLES.has(String(role || '').trim().toLowerCase());
}

const ADD_ITEMS = Object.freeze({
  admin: [ADD_TASK_ITEM],
  dispatcher: [ADD_TASK_ITEM],
  customer: [ADD_TASK_ITEM],
  clerk: [ADD_TASK_ITEM],
  finance: [ADD_TASK_ITEM],
});

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function clone(items) {
  return items.map((item) => ({ ...item }));
}

export function navigationItemsForRole(role) {
  return clone(NAVIGATION[role] ?? []);
}

export function addLauncherItemsForRole(role) {
  return clone(ADD_ITEMS[role] ?? []);
}


function iconButtonContent(item, iconFor) {
  return `<span class="icon-btn__back icon-btn__back--${escapeHtml(item.color)}"></span><span class="icon-btn__front"><span class="icon-btn__icon" aria-hidden="true">${iconFor(item.icon)}</span></span><span class="icon-btn__label">${escapeHtml(item.label)}</span>`;
}

export function topGlassNavigationMarkup({ role, currentHash = '', iconFor = () => '', locale = 'en' } = {}) {
  const navigation = navigationItemsForRole(role).map((item) => {
    const localizedItem = { ...item, label: t(item.labelKey, {}, locale) };
    const active = String(currentHash).split('?')[0] === item.route ? ' active' : '';
    return `<a class="icon-btn${active}" href="${item.route}" aria-label="${escapeHtml(localizedItem.label)}">${iconButtonContent(localizedItem, iconFor)}</a>`;
  }).join('');
  const addItems = addLauncherItemsForRole(role).map((item) => ({
    ...item,
    label: t(item.labelKey, {}, locale),
  }));
  const addLabel = t('nav.add', {}, locale);
  // One item is not a menu. The button opens the Task tab on the click (Alan,
  // 2026-08-09: "点击直接回弹出tab"), so there is nothing to expand and nothing to
  // read before pressing it.
  const bulkLabel = t(BULK_ADD_ITEM.labelKey, {}, locale);
  const bulk = bulkAddAvailableForRole(role)
    ? `<button class="icon-btn top-bulk-add" type="button" data-top-bulk-route="${BULK_ADD_ITEM.route}" aria-label="${escapeHtml(bulkLabel)}">${iconButtonContent({ ...BULK_ADD_ITEM, label: bulkLabel }, iconFor)}</button>`
    : '';
  const launcher = addItems.length ? `
    <div class="top-add-launcher" data-top-add-launcher>
      <button class="icon-btn top-add-launcher__toggle" type="button" data-top-add-route="${addItems[0].route}" aria-label="${escapeHtml(addItems[0].label || addLabel)}">${iconButtonContent({ label: addItems[0].label || addLabel, icon: 'add', color: 'orange' }, iconFor)}</button>
      ${bulk}
    </div>` : '';
  return `<nav class="top-glass-navigation" aria-label="${escapeHtml(t('shell.primaryNavigation', {}, locale))}">${navigation}${launcher}</nav>`;
}

export function mountTopAddLauncher(root, { onSelect = () => {} } = {}) {
  const host = root?.querySelector?.('[data-top-add-launcher]');
  const toggle = host?.querySelector?.('[data-top-add-route]');
  if (!host || !toggle) return Object.freeze({ destroy() {}, close() {} });
  const controller = new AbortController();
  // No menu to open or close any more, so close() is kept only because callers
  // still hold the handle and call it on navigation.
  toggle.addEventListener('click', () => onSelect(toggle.dataset.topAddRoute), {
    signal: controller.signal,
  });
  host.querySelector('[data-top-bulk-route]')?.addEventListener('click', (event) => {
    onSelect(event.currentTarget.dataset.topBulkRoute);
  }, { signal: controller.signal });
  return Object.freeze({ close() {}, destroy: () => controller.abort() });
}
